import { mdiClockOutline, mdiMagnify, mdiOpenInNew, mdiRefresh } from "@mdi/js";
import numeral from "numeral";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "../../../ui/components/button/Button";
import { CollectionTile } from "../../../ui/components/collectiontile/CollectionTile";
import { CollectionTileSkeleton } from "../../../ui/components/collectiontile/CollectionTileSkeleton";
import { Input } from "../../../ui/components/form/input/Input";
import { Listing } from "../../../ui/components/listing/Listing";
import { NoResults } from "../../../ui/components/no_results/NoResults";
import { Pagination } from "../../../ui/components/pagination/Pagination";
import { Picker } from "../../../ui/components/picker/Picker";
import { TabButton } from "../../../ui/components/tabs/Tab";
import { TabBar } from "../../../ui/components/tabs/TabBar";
import { TabPanel } from "../../../ui/components/tabs/TabPanel";
import { TabProvider } from "../../../ui/components/tabs/tabs.context";
import { Typography } from "../../../ui/components/typography/Typography";
import { UserCanceled } from "../../../util/api";
import { activeGameId } from "../../../util/selectors";
import MainPage from "../../../views/MainPage";
import { CollectionsDownloadClickedEvent } from "../../analytics/mixpanel/MixpanelEvents";
import { getGame } from "../../gamemode_management/util/getGame";
import { nexusGameId } from "../../nexus_integration/util/convertGameId";
const SORT_OPTIONS = [
    { field: "createdAt", direction: "DESC", label: "Recently Listed" },
    { field: "endorsements", direction: "DESC", label: "Most Endorsed" },
    { field: "recentRating", direction: "DESC", label: "Highest Rated" },
    { field: "downloads", direction: "DESC", label: "Most Downloaded" },
];
async function adultContentDialog(api, collection, adultContent) {
    try {
        const result = await api.showDialog("question", "Adult content warning", {
            bbcode: api.translate(`The collection "[b]{{collectionName}}[/b]" has been classified as adult content because it contains: nudity, sexualisation, extreme violence and gore, or excessive profanity.<br/><br/>` +
                `Your site preferences are set to hide adult content, you can update your preferences on the Nexus Mods website.`, { replace: { collectionName: collection.name } }),
        }, [
            { label: api.translate("Cancel") },
            { label: api.translate("Open site preferences") },
        ]);
        return result.action === "Cancel" ? false : true;
    }
    catch {
        return adultContent;
    }
}
function BrowseNexusPage(props) {
    const { api } = props;
    const t = (input, options) => api.translate(input, {
        isNamespaceKey: true,
        ns: ["collection", "common"],
        ...options,
    });
    const scrollRef = useRef(null);
    const gameId = useSelector((state) => activeGameId(state));
    const gameDomainName = nexusGameId(getGame(gameId), gameId);
    const isLoggedIn = useSelector((state) => {
        const nexus = state.confidential?.account?.["nexus"];
        return !!(nexus?.APIKey || nexus?.OAuthCredentials);
    });
    const adultContentFilter = useSelector((state) => state.persistent["nexus"]?.userInfo?.adult);
    const [collections, setCollections] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [allCollectionsTotal, setAllCollectionsTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState(SORT_OPTIONS[1]); // Default to "Most Endorsed"
    const [searchQuery, setSearchQuery] = useState("");
    const [activeSearch, setActiveSearch] = useState(""); // The search term actually being used
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState("collections");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [searchValidationError, setSearchValidationError] = React.useState("");
    const itemsPerPage = 20;
    const handleSearch = () => {
        // Clear any previous validation errors
        setSearchValidationError("");
        // If search query is too short (1 character), show validation error
        if (searchQuery.length === 1) {
            setSearchValidationError(t("collection:browse.searchTooShort"));
            return;
        }
        // If search query is empty, clear the search
        // If search query is 2+ characters, perform the search
        setActiveSearch(searchQuery);
        setCurrentPage(1);
    };
    const handleRefresh = () => {
        setLoading(true);
        setError(null);
        setRefreshTrigger((prev) => prev + 1);
    };
    const handleAddCollection = (collection) => {
        const revisionNumber = collection.latestPublishedRevision?.revisionNumber || "latest";
        // Use the game domain name from the collection data (already converted)
        const nxmUrl = `nxm://${collection.game.domainName}/collections/${collection.slug}/revisions/${revisionNumber}`;
        // Track the download click event
        api.events.emit("analytics-track-mixpanel-event", new CollectionsDownloadClickedEvent(collection.slug, collection.game.id));
        if (adultContentFilter === false &&
            collection.latestPublishedRevision?.adultContent) {
            void adultContentDialog(api, collection, false).then((proceed) => {
                if (proceed) {
                    window.api.shell.openUrl("https://next.nexusmods.com/settings/content-blocking");
                }
            });
        }
        else {
            // Use the Vortex API to handle the NXM link.
            // Populate modInfo.nexus.ids so the persisted download is recognised as
            // a collection by `nexusIdsFromDownloadId` — without these fields the
            // download-completed analytics in DownloadObserver can't tell it apart
            // from a non-Nexus download and silently skips the event.
            api.events.emit("start-download", [nxmUrl], {
                game: gameId,
                source: "nexus",
                name: collection.name,
                nexus: {
                    ids: {
                        gameId: collection.game.domainName,
                        collectionId: collection.id,
                        revisionId: collection.latestPublishedRevision?.id,
                        collectionSlug: collection.slug,
                        revisionNumber: collection.latestPublishedRevision?.revisionNumber,
                    },
                },
            }, undefined, (err) => {
                if (err && !(err instanceof UserCanceled)) {
                    api.showErrorNotification("Failed to add collection", err);
                }
            }, undefined, { allowInstall: "force" });
        }
    };
    useEffect(() => {
        if (!gameId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        const options = {
            gameId: gameDomainName,
            count: 20,
            offset: (currentPage - 1) * itemsPerPage,
            sort: {
                field: sortBy.field,
                direction: sortBy.direction,
            },
            search: activeSearch || undefined,
            categoryName: [{ op: "NOT_EQUALS", value: "Wabbajack Mod List" }],
            collectionStatuses: ["listed"],
        };
        // Fetch collections using the new search API with sorting and search
        Promise.resolve(api.ext.nexusSearchCollections(options))
            .then((result) => {
            setCollections(result.nodes || []);
            setTotalCount(result.totalCount || 0);
            // Store unfiltered total when no search is active
            if (!activeSearch) {
                setAllCollectionsTotal(result.totalCount || 0);
            }
            setLoading(false);
        })
            .catch((err) => {
            // Provide user-friendly error message for wildcard search requirement
            if (err.message &&
                err.message.includes("Wildcard value must have 2 or more characters")) {
                const friendlyError = new Error(t("collection:browse.searchTooShort"));
                setError(friendlyError);
                // Also clear the invalid search so user can try again
                setActiveSearch("");
            }
            else {
                setError(err);
            }
            setLoading(false);
        });
    }, [gameId, sortBy, activeSearch, currentPage, api, refreshTrigger]);
    if (!gameId) {
        return (React.createElement(MainPage, { id: "browse-collections-page" },
            React.createElement(MainPage.Body, null,
                React.createElement("div", { className: "p-5 text-center" },
                    React.createElement("p", null, t("collection:browse.selectGame"))))));
    }
    return (React.createElement(MainPage, { id: "browse-collections-page" },
        React.createElement(MainPage.Body, { className: "h-full overflow-y-auto pt-6", ref: scrollRef },
            React.createElement(TabProvider, { tab: selectedTab, tabListId: "browse-nexus-tabs", onSetSelectedTab: setSelectedTab },
                React.createElement(TabBar, { className: "pl-6" },
                    React.createElement(TabButton, { count: allCollectionsTotal, name: t("collection:browse.tabs.collections") }),
                    React.createElement(TabButton, { name: t("collection:browse.tabs.mods") })),
                React.createElement(TabPanel, { name: t("collection:browse.tabs.collections") },
                    React.createElement("div", { className: "space-y-3 p-6" },
                        React.createElement("form", { className: "flex items-center gap-x-2", onSubmit: (e) => {
                                e.preventDefault();
                                handleSearch();
                            } },
                            React.createElement(Input, { errorMessage: searchValidationError || undefined, fieldClassName: "max-w-60", hideLabel: true, label: t("collection:browse.searchPlaceholder"), placeholder: t("collection:browse.searchPlaceholder"), size: "sm", value: searchQuery, onChange: (e) => {
                                    setSearchQuery(e.target.value);
                                    // Clear validation error when user types
                                    if (searchValidationError) {
                                        setSearchValidationError("");
                                    }
                                } }),
                            React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiMagnify, size: "sm", title: t("common:actions.search"), type: "submit" })),
                        React.createElement("div", { className: "flex justify-between" },
                            React.createElement("div", { className: "flex items-center gap-x-2" },
                                React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiRefresh, size: "sm", title: t("collection:browse.refresh"), onClick: handleRefresh }),
                                React.createElement(Typography, { appearance: "moderate", isTranslucent: true, typographyType: "body-sm" }, t("collection:browse.resultsCount", {
                                    total: numeral(totalCount).format("0,0"),
                                }))),
                            React.createElement(Picker, { options: SORT_OPTIONS.map((option) => ({
                                    label: option.label,
                                    value: option,
                                })), value: sortBy, onChange: (value) => {
                                    setSortBy(value);
                                    setCurrentPage(1);
                                } })),
                        React.createElement(Listing, { className: "grid grid-cols-[repeat(auto-fit,minmax(26rem,1fr))] gap-4", entityCount: collections?.length, isError: !!error, isLoading: loading, noResultsChildren: React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiOpenInNew, size: "sm", onClick: () => window.api.shell.openUrl(`https://www.nexusmods.com/games/${gameDomainName}/mods`) }, t("collection:browse.modsComingSoon.openWebsite")), noResultsMessage: t("collection:browse.noCollections.message"), noResultsTitle: t("collection:browse.noCollections.title"), skeletonCount: 12, SkeletonTile: CollectionTileSkeleton }, collections?.map((collection) => (React.createElement(CollectionTile, { api: api, collection: collection, isLoggedIn: isLoggedIn, key: collection.id, onAddCollection: () => handleAddCollection(collection), onViewPage: () => window.api.shell.openUrl(`https://www.nexusmods.com/games/${collection.game.domainName}/collections/${collection.slug}`) })))),
                        React.createElement(Pagination, { currentPage: currentPage, recordsPerPage: itemsPerPage, scrollRef: scrollRef, totalRecords: totalCount, onPaginationUpdate: (newPage) => setCurrentPage(newPage) }))),
                React.createElement(TabPanel, { name: t("collection:browse.tabs.mods") },
                    React.createElement(NoResults, { className: "py-16", iconPath: mdiClockOutline, message: t("collection:browse.modsComingSoon.description"), title: t("collection:browse.modsComingSoon.title") },
                        React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiOpenInNew, size: "sm", onClick: () => window.api.shell.openUrl(`https://www.nexusmods.com/games/${gameDomainName}/mods`) }, t("collection:browse.modsComingSoon.openWebsite"))))))));
}
export default BrowseNexusPage;
